import CloseIcon from "@mui/icons-material/Close"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import LanguageIcon from "@mui/icons-material/Language"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material"
import { useEffect, useState } from "react"

import Button from "@components/button"
import Switch from "@components/switch"
import { GRAY_COLOR, PRIMARY, WATSAPP_ACTIVE } from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import {
  listWhatsAppAccounts,
  listWhatsAppTemplates,
  updateSettingsReq,
  uploadAnnouncementMediaReq
} from "@domain/changelog"
import {
  ChangelogWhatsappParamSource,
  ChangelogWhatsappTemplateEntry,
  ChangelogWhatsappType,
  WhatsAppBusinessAccount,
  WhatsAppBusinessTemplate
} from "@models/changelog"
import { useQuery } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"

import { langFlag } from "../shared"

const AVAILABLE_LANGS = ["tr", "en", "de", "fr", "ar"]
const LANG_LABEL: Record<string, string> = {
  tr: "Türkçe",
  en: "English",
  de: "Deutsch",
  fr: "Français",
  ar: "العربية"
}
const TIMEZONES = [
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "UTC"
]
const DATE_FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD"]
const WHATSAPP_BUSINESS = "WHATS_APP_BUSINESS"
const META_WHATSAPP_BUSINESS = "META_WHATS_APP_BUSINESS"
const WHATSAPP_WEB = "WHATS_APP_WEB"
const PARAM_SOURCES: ChangelogWhatsappParamSource[] = [
  "title",
  "shortDescription",
  "content",
  "category",
  "coverImage",
  "announcementId"
]

export default function SettingsSection() {
  const { translate, settings, settingsLoading, refetchSettings } =
    useChangelogPageContext()

  const [defaultLanguage, setDefaultLanguage] = useState("en")
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([
    "tr",
    "en"
  ])
  const [defaultTimezone, setDefaultTimezone] = useState("Europe/Istanbul")
  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY")
  const [fromAddress, setFromAddress] = useState("")
  const [fromName, setFromName] = useState("Qpien Changelog")
  const [whatsappType, setWhatsappType] =
    useState<ChangelogWhatsappType>(WHATSAPP_BUSINESS)
  const [senderCustomerId, setSenderCustomerId] = useState("")
  const [accountId, setAccountId] = useState("")
  const [whatsappTemplates, setWhatsappTemplates] = useState<
    ChangelogWhatsappTemplateEntry[]
  >([])
  const [webMessage, setWebMessage] = useState("")
  const [webImageUrl, setWebImageUrl] = useState("")
  const [webImageType, setWebImageType] = useState("")
  const [imageUploading, setImageUploading] = useState(false)
  const [includeInactiveCustomers, setIncludeInactiveCustomers] =
    useState(false)
  const [includeArchivedPersons, setIncludeArchivedPersons] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!settings) {
      return
    }
    setDefaultLanguage(settings.defaultLanguage || "en")
    setSupportedLanguages(settings.supportedLanguages || ["tr", "en"])
    setDefaultTimezone(settings.defaultTimezone || "Europe/Istanbul")
    setDateFormat(settings.dateFormat || "DD/MM/YYYY")
    setFromAddress(settings.senderEmail?.fromAddress || "")
    setFromName(settings.senderEmail?.fromName || "Qpien Changelog")
    setWhatsappType(settings.senderWhatsapp?.type || WHATSAPP_BUSINESS)
    setSenderCustomerId(settings.senderCustomerId || "")
    setAccountId(settings.senderWhatsapp?.accountId || "")
    setWhatsappTemplates(settings.senderWhatsapp?.templates || [])
    setWebMessage(settings.senderWhatsapp?.webMessage || "")
    setWebImageUrl(settings.senderWhatsapp?.webImageUrl || "")
    setWebImageType(settings.senderWhatsapp?.webImageType || "")
    // Back-compat: derive from the legacy recipientAudience when the booleans are absent.
    setIncludeInactiveCustomers(
      settings.includeInactiveCustomers ?? settings.recipientAudience === "ALL"
    )
    setIncludeArchivedPersons(settings.includeArchivedPersons ?? false)
  }, [settings])

  const accountsQuery = useQuery({
    queryKey: ["changelog-wa-accounts"],
    queryFn: async () => (await listWhatsAppAccounts()).data ?? []
  })
  const templatesQuery = useQuery({
    queryKey: ["changelog-wa-templates", whatsappType, accountId],
    queryFn: async () =>
      (await listWhatsAppTemplates({ type: whatsappType, accountId })).data ??
      [],
    enabled: whatsappType !== WHATSAPP_WEB && !!accountId
  })

  const accounts = (accountsQuery.data || []).filter(
    (account) => account.type === whatsappType
  )

  const addableLangs = AVAILABLE_LANGS.filter(
    (l) => !supportedLanguages.includes(l)
  )

  const selectTemplateForLanguage = (
    language: string,
    providerTemplateId: string
  ) => {
    const template = (templatesQuery.data || []).find(
      (item) => item.templateId === providerTemplateId
    )
    if (!template) {
      setWhatsappTemplates((previous) =>
        previous.filter(
          (entry) => !languageMatches(language, entry.languageCode)
        )
      )
      return
    }
    const customFields = [...(template.qpienFields || [])]
      .filter((field) => field.ref === "custom_params")
      .sort((left, right) => left.key - right.key)
    const defaults: ChangelogWhatsappParamSource[] = [
      "title",
      "content",
      "shortDescription"
    ]
    const entry: ChangelogWhatsappTemplateEntry = {
      languageCode: template.languageCode || language,
      templateId: template.templateId,
      paramMap: customFields.map((field, index) => ({
        key: field.key,
        source: defaults[index] || "title"
      }))
    }
    setWhatsappTemplates((previous) => [
      ...previous.filter(
        (current) => !languageMatches(language, current.languageCode)
      ),
      entry
    ])
  }

  const setParamSource = (
    language: string,
    key: number,
    source: ChangelogWhatsappParamSource
  ) => {
    setWhatsappTemplates((previous) =>
      previous.map((entry) =>
        languageMatches(language, entry.languageCode)
          ? {
              ...entry,
              paramMap: entry.paramMap.map((mapping) =>
                mapping.key === key ? { ...mapping, source } : mapping
              )
            }
          : entry
      )
    )
  }

  const onSave = async () => {
    setSaving(true)
    try {
      const res = await updateSettingsReq({
        defaultLanguage,
        supportedLanguages,
        defaultTimezone,
        dateFormat,
        senderEmail: { fromAddress, fromName },
        senderCustomerId: senderCustomerId.trim() || undefined,
        senderWhatsapp:
          whatsappType === WHATSAPP_WEB
            ? {
                type: whatsappType,
                accountId: accountId || undefined,
                webMessage: webMessage || undefined,
                webImageUrl: webImageUrl || undefined,
                webImageType: webImageType || undefined
              }
            : {
                type: whatsappType,
                accountId: accountId || undefined,
                templates: supportedLanguages
                  .map((languageCode) =>
                    whatsappTemplates.find((entry) =>
                      languageMatches(languageCode, entry.languageCode)
                    )
                  )
                  .filter(
                    (entry): entry is ChangelogWhatsappTemplateEntry => !!entry
                  )
              },
        includeInactiveCustomers,
        includeArchivedPersons
      })
      if (res.isSuccess) {
        showSuccessToast(translate("changelog_settings_saved"))
        refetchSettings()
      } else {
        showErrorToast(res.errorMessage || translate("changelog_save_failed"))
      }
    } finally {
      setSaving(false)
    }
  }

  const uploadWebImage = async (file?: File) => {
    if (!file) {
      return
    }
    if (!file.type.startsWith("image/")) {
      showErrorToast(translate("changelog_wa_web_image_invalid"))
      return
    }
    setImageUploading(true)
    try {
      const response = await uploadAnnouncementMediaReq(file)
      if (!response.isSuccess || !response.data?.url) {
        showErrorToast(
          response.errorMessage || translate("changelog_save_failed")
        )
        return
      }
      setWebImageUrl(response.data.url)
      setWebImageType(file.type)
    } finally {
      setImageUploading(false)
    }
  }

  if (settingsLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={26} />
      </Stack>
    )
  }

  return (
    <Box sx={{ maxWidth: "52rem" }}>
      <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, mb: 2 }}>
        {translate("changelog_settings")}
      </Typography>

      {/* Language & time */}
      <Card
        icon={<LanguageIcon fontSize="small" />}
        title={translate("changelog_settings_lang_time")}
      >
        <Label text={translate("changelog_default_language")} />
        <Select
          size="small"
          fullWidth
          value={defaultLanguage}
          onChange={(e) => setDefaultLanguage(e.target.value)}
          sx={{ mb: 2 }}
        >
          {supportedLanguages.map((l) => (
            <MenuItem key={l} value={l}>
              {langFlag(l)} {LANG_LABEL[l] || l}
            </MenuItem>
          ))}
        </Select>

        <Label text={translate("changelog_supported_languages")} />
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {supportedLanguages.map((l) => (
            <Chip
              key={l}
              label={`${langFlag(l)} ${LANG_LABEL[l] || l}`}
              onDelete={
                supportedLanguages.length > 1
                  ? () =>
                      setSupportedLanguages((prev) =>
                        prev.filter((x) => x !== l)
                      )
                  : undefined
              }
              deleteIcon={<CloseIcon />}
              sx={{ background: "#EEF4FF", color: PRIMARY, fontWeight: 500 }}
            />
          ))}
          {addableLangs.length > 0 && (
            <Select
              size="small"
              displayEmpty
              value=""
              onChange={(e) => {
                if (e.target.value) {
                  setSupportedLanguages((prev) => [...prev, e.target.value])
                }
              }}
              sx={{ minWidth: "8rem", height: "2rem" }}
              renderValue={() => `+ ${translate("changelog_add_language")}`}
            >
              {addableLangs.map((l) => (
                <MenuItem key={l} value={l}>
                  {langFlag(l)} {LANG_LABEL[l] || l}
                </MenuItem>
              ))}
            </Select>
          )}
        </Stack>

        <Stack direction="row" gap={2}>
          <Box sx={{ flex: 1 }}>
            <Label text={translate("changelog_timezone")} />
            <Select
              size="small"
              fullWidth
              value={defaultTimezone}
              onChange={(e) => setDefaultTimezone(e.target.value)}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Label text={translate("changelog_date_format")} />
            <Select
              size="small"
              fullWidth
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
            >
              {DATE_FORMATS.map((df) => (
                <MenuItem key={df} value={df}>
                  {df}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      </Card>

      {/* Audience */}
      <Card
        icon={<GroupsOutlinedIcon fontSize="small" />}
        title={translate("changelog_settings_audience")}
      >
        <Stack gap={1.5}>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                {translate("changelog_audience_inactive_label")}
              </Typography>
              <Switch
                checked={includeInactiveCustomers}
                onChange={(e) => setIncludeInactiveCustomers(e.target.checked)}
              />
            </Stack>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              {translate("changelog_audience_inactive_hint")}
            </Typography>
          </Box>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                {translate("changelog_audience_archived_label")}
              </Typography>
              <Switch
                checked={includeArchivedPersons}
                onChange={(e) => setIncludeArchivedPersons(e.target.checked)}
              />
            </Stack>
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              {translate("changelog_audience_archived_hint")}
            </Typography>
          </Box>
        </Stack>
      </Card>

      {/* Email sender */}
      <Card
        icon={<EmailOutlinedIcon fontSize="small" />}
        title={translate("changelog_settings_email_sender")}
      >
        <Stack direction="row" gap={2}>
          <Box sx={{ flex: 1 }}>
            <Label text={translate("changelog_from_address")} />
            <TextField
              size="small"
              fullWidth
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              placeholder="duyuru@qpien.com"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Label text={translate("changelog_from_name")} />
            <TextField
              size="small"
              fullWidth
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Qpien Changelog"
            />
          </Box>
        </Stack>
      </Card>

      {/* WhatsApp sender */}
      <Card
        icon={<WhatsAppIcon fontSize="small" sx={{ color: WATSAPP_ACTIVE }} />}
        title={translate("changelog_settings_whatsapp")}
      >
        <Label text={translate("changelog_wa_sender_customer")} />
        <TextField
          size="small"
          fullWidth
          value={senderCustomerId}
          onChange={(e) => setSenderCustomerId(e.target.value)}
          placeholder={translate("changelog_wa_sender_customer_ph")}
          helperText={translate("changelog_wa_sender_customer_hint")}
          sx={{ mb: 2 }}
        />

        <Label text={translate("changelog_wa_type")} />
        <Select
          size="small"
          fullWidth
          value={whatsappType}
          onChange={(e) => {
            setWhatsappType(e.target.value as ChangelogWhatsappType)
            setAccountId("")
            setWhatsappTemplates([])
          }}
          sx={{ mb: 2 }}
        >
          <MenuItem value={WHATSAPP_BUSINESS}>
            {translate("changelog_wa_type_business")}
          </MenuItem>
          <MenuItem value={META_WHATSAPP_BUSINESS}>
            {translate("changelog_wa_type_meta")}
          </MenuItem>
          <MenuItem value={WHATSAPP_WEB}>
            {translate("changelog_wa_type_web")}
          </MenuItem>
        </Select>

        <Label text={translate("changelog_wa_account")} />
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value)
            setWhatsappTemplates([])
          }}
          sx={{ mb: 2 }}
          renderValue={(v) =>
            v
              ? accountLabel(accounts.find((account) => account._id === v))
              : translate("changelog_wa_account_ph")
          }
        >
          {accounts.map((a: WhatsAppBusinessAccount) => (
            <MenuItem key={a._id} value={a._id}>
              {accountLabel(a)}
            </MenuItem>
          ))}
        </Select>

        {whatsappType === WHATSAPP_WEB ? (
          <>
            <Label text={translate("changelog_wa_web_message")} />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={5}
              value={webMessage}
              onChange={(e) => setWebMessage(e.target.value)}
              placeholder={translate("changelog_wa_web_message_ph")}
              helperText={translate("changelog_wa_web_message_hint")}
              sx={{ mb: 2 }}
            />

            <Label text={translate("changelog_wa_web_image")} />
            <Stack direction="row" alignItems="center" gap={2}>
              <Box
                component="label"
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  cursor: imageUploading ? "default" : "pointer",
                  color: PRIMARY,
                  fontSize: "0.85rem",
                  fontWeight: 600
                }}
              >
                {imageUploading
                  ? translate("changelog_wa_web_image_uploading")
                  : translate("changelog_wa_web_image_pick")}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  disabled={imageUploading}
                  onChange={(event) => {
                    void uploadWebImage(event.target.files?.[0])
                    event.target.value = ""
                  }}
                />
              </Box>
              {webImageUrl && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    component="img"
                    src={webImageUrl}
                    alt={translate("changelog_wa_web_image")}
                    sx={{
                      width: 72,
                      height: 72,
                      objectFit: "cover",
                      borderRadius: 1
                    }}
                  />
                  <CloseIcon
                    onClick={() => {
                      setWebImageUrl("")
                      setWebImageType("")
                    }}
                    sx={{ cursor: "pointer", color: "text.secondary" }}
                  />
                </Stack>
              )}
            </Stack>
          </>
        ) : (
          <Stack gap={2}>
            {supportedLanguages.map((language) => {
              const languageTemplates = (templatesQuery.data || []).filter(
                (template) =>
                  languageMatches(language, template.languageCode || "")
              )
              const entry = whatsappTemplates.find((current) =>
                languageMatches(language, current.languageCode)
              )
              const selectedTemplate = languageTemplates.find(
                (template) => template.templateId === entry?.templateId
              )
              const customFields = [...(selectedTemplate?.qpienFields || [])]
                .filter((field) => field.ref === "custom_params")
                .sort((left, right) => left.key - right.key)
              return (
                <Box
                  key={language}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                    p: 2
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 1.5 }}
                  >
                    {langFlag(language)}{" "}
                    {LANG_LABEL[language] || language.toUpperCase()}
                  </Typography>
                  <Label text={translate("changelog_wa_language_template")} />
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={entry?.templateId || ""}
                    onChange={(event) =>
                      selectTemplateForLanguage(language, event.target.value)
                    }
                    renderValue={(value) =>
                      value
                        ? templateLabel(
                            languageTemplates.find(
                              (template) => template.templateId === value
                            )
                          )
                        : translate("changelog_wa_template_ph")
                    }
                  >
                    {languageTemplates.map(
                      (template: WhatsAppBusinessTemplate) => (
                        <MenuItem
                          key={template._id}
                          value={template.templateId}
                        >
                          {templateLabel(template)}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {customFields.length > 0 && (
                    <Stack gap={1.5} sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
                        {translate("changelog_wa_variable_mapping")}
                      </Typography>
                      {customFields.map((field) => (
                        <Box key={field.key}>
                          <Label
                            text={`{{${field.key}}} — ${field.value || field.exampleData || ""}`}
                          />
                          <Select
                            size="small"
                            fullWidth
                            value={
                              entry?.paramMap.find(
                                (mapping) => mapping.key === field.key
                              )?.source || "title"
                            }
                            onChange={(event) =>
                              setParamSource(
                                language,
                                field.key,
                                event.target
                                  .value as ChangelogWhatsappParamSource
                              )
                            }
                          >
                            {PARAM_SOURCES.map((source) => (
                              <MenuItem key={source} value={source}>
                                {translate(`changelog_wa_source_${source}`)}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              )
            })}
          </Stack>
        )}
      </Card>

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button loading={saving} onClick={onSave}>
          {translate("changelog_save_settings")}
        </Button>
      </Stack>
    </Box>
  )
}

function accountLabel(a?: WhatsAppBusinessAccount): string {
  if (!a) {
    return ""
  }
  return a.name || a.appName || a.number || a.channelId || a._id
}

function templateLabel(t?: WhatsAppBusinessTemplate): string {
  if (!t) {
    return ""
  }
  return `${t.name}${t.status ? ` — ${t.status}` : ""}`
}

function languageMatches(left: string, right: string): boolean {
  const normalize = (value: string) =>
    value.trim().toLowerCase().replace("-", "_").split("_")[0]
  return !!left && !!right && normalize(left) === normalize(right)
}

function Card(props: {
  icon: JSX.Element
  title: string
  children: React.ReactNode
}) {
  return (
    <Box
      sx={{
        background: "white",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        p: 3,
        mb: 2.5
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
        <Box sx={{ color: GRAY_COLOR, display: "flex" }}>{props.icon}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
          {props.title}
        </Typography>
      </Stack>
      {props.children}
    </Box>
  )
}

function Label({ text }: { text: string }) {
  return (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        mb: 0.75,
        color: "text.secondary"
      }}
    >
      {text}
    </Typography>
  )
}
